(() => {
  'use strict';

  const instances = new WeakMap();
  let activeInstance = null;

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const normalizeAngle = value => ((value + 180) % 360 + 360) % 360 - 180;
  const radians = degrees => degrees * Math.PI / 180;
  const isPowerOfTwo = value => value > 0 && (value & (value - 1)) === 0;
  const isPowerOfTwoTexture = source => isPowerOfTwo(source.width) && isPowerOfTwo(source.height);

  const resolvePano = target => {
    if (!target) return null;
    if (target.matches?.('[data-kgmj-sphere="true"],[data-kgm-room-360]')) return target;
    return target.querySelector?.('[data-kgmj-sphere="true"],[data-kgm-room-360]') || null;
  };

  const initialState = pano => {
    const scene = pano.closest?.('[data-kgmj-scene]');
    return {
      yaw: Number(scene?.dataset.initialYaw ?? pano.dataset.initialYaw ?? 0),
      pitch: Number(scene?.dataset.initialPitch ?? pano.dataset.initialPitch ?? 0),
      zoom: Number(scene?.dataset.initialZoom ?? pano.dataset.initialZoom ?? 0.68)
    };
  };

  const sanitizeState = (pano, state) => {
    const defaults = initialState(pano);
    const candidate = state || pano._kgmLast360State || defaults;
    candidate.yaw = normalizeAngle(Number.isFinite(Number(candidate.yaw)) ? Number(candidate.yaw) : defaults.yaw);
    candidate.pitch = clamp(Number.isFinite(Number(candidate.pitch)) ? Number(candidate.pitch) : defaults.pitch, -72, 72);
    candidate.zoom = clamp(Number.isFinite(Number(candidate.zoom)) ? Number(candidate.zoom) : defaults.zoom, 0.68, 2.2);
    return candidate;
  };

  const getAnchors = (pano, elements) => {
    if (elements && typeof elements[Symbol.iterator] === 'function') return [...elements].filter(Boolean);
    return [...pano.querySelectorAll('.kgmj-hotspot,[data-kgmj-room-product]')];
  };

  const showFallback = (pano, message, reason, error) => {
    pano.classList.remove('is-ready', 'is-loading');
    pano.classList.add('is-error');
    pano.setAttribute('aria-busy', 'false');
    const loader = pano.querySelector('[data-kgmj-sphere-loading]');
    if (loader) {
      loader.textContent = message || 'Panoramic view unavailable';
      loader.hidden = true;
      loader.setAttribute('aria-hidden', 'true');
    }
    const fallback = pano.querySelector('[data-kgmj-sphere-fallback]');
    if (fallback) {
      fallback.hidden = false;
      fallback.setAttribute('aria-hidden', 'false');
    }
    pano.dispatchEvent(new CustomEvent('kgm:room360-error', {
      bubbles: true,
      detail: { reason: reason || 'unknown', error: error || null }
    }));
  };

  const showLoading = pano => {
    pano.classList.remove('is-ready', 'is-error', 'is-context-lost');
    pano.classList.add('is-loading');
    pano.setAttribute('aria-busy', 'true');
    const loader = pano.querySelector('[data-kgmj-sphere-loading]');
    if (loader) {
      loader.hidden = false;
      loader.removeAttribute('aria-hidden');
      loader.textContent = loader.dataset.loadingText || 'Preparing panoramic room';
    }
    const fallback = pano.querySelector('[data-kgmj-sphere-fallback]');
    if (fallback) {
      fallback.hidden = true;
      fallback.setAttribute('aria-hidden', 'true');
    }
  };

  const compileShader = (gl, type, source) => {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const message = gl.getShaderInfoLog(shader) || 'Unable to compile panoramic shader.';
      gl.deleteShader(shader);
      throw new Error(message);
    }
    return shader;
  };

  const createProgram = gl => {
    const vertex = compileShader(gl, gl.VERTEX_SHADER, `
      attribute vec2 a_position;
      varying vec2 v_position;
      void main() {
        v_position = a_position;
        gl_Position = vec4(a_position, 0.0, 1.0);
      }
    `);
    const fragment = compileShader(gl, gl.FRAGMENT_SHADER, `
      precision highp float;
      varying vec2 v_position;
      uniform sampler2D u_texture;
      uniform float u_aspect;
      uniform float u_vfov;
      uniform float u_yaw;
      uniform float u_pitch;
      const float PI = 3.1415926535897932384626433832795;

      void main() {
        float tangent = tan(u_vfov * 0.5);
        vec3 direction = normalize(vec3(
          v_position.x * u_aspect * tangent,
          v_position.y * tangent,
          1.0
        ));

        float cp = cos(u_pitch);
        float sp = sin(u_pitch);
        direction = vec3(
          direction.x,
          cp * direction.y + sp * direction.z,
          -sp * direction.y + cp * direction.z
        );

        float cy = cos(u_yaw);
        float sy = sin(u_yaw);
        direction = vec3(
          cy * direction.x + sy * direction.z,
          direction.y,
          -sy * direction.x + cy * direction.z
        );

        float longitude = atan(direction.x, direction.z);
        float latitude = asin(clamp(direction.y, -1.0, 1.0));
        vec2 uv = vec2(fract(0.5 + longitude / (2.0 * PI)), 0.5 + latitude / PI);
        gl_FragColor = texture2D(u_texture, uv);
      }
    `);

    const program = gl.createProgram();
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    const linked = gl.getProgramParameter(program, gl.LINK_STATUS);
    const message = gl.getProgramInfoLog(program);
    gl.detachShader(program, vertex);
    gl.detachShader(program, fragment);
    gl.deleteShader(vertex);
    gl.deleteShader(fragment);
    if (!linked) {
      gl.deleteProgram(program);
      throw new Error(message || 'Unable to link panoramic shader.');
    }
    return program;
  };

  const resizeCanvas = instance => {
    if (!instance || instance.disposed) return false;
    const { canvas, gl } = instance;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.max(2, Math.round(rect.width * dpr));
    const height = Math.max(2, Math.round(rect.height * dpr));
    if (canvas.width === width && canvas.height === height) return false;
    canvas.width = width;
    canvas.height = height;
    gl.viewport(0, 0, width, height);
    return true;
  };

  const fitTextureSource = (image, limit) => {
    if (image.naturalWidth <= limit && image.naturalHeight <= limit) return image;
    const scale = Math.min(limit / image.naturalWidth, limit / image.naturalHeight);
    let width = Math.max(2, Math.floor(image.naturalWidth * scale));
    let height = Math.max(2, Math.floor(image.naturalHeight * scale));
    // Preserve a precise 2:1 shape while staying within the GPU limit.
    if (image.naturalWidth / image.naturalHeight > 1.95) {
      width = Math.min(limit, width);
      height = Math.max(2, Math.floor(width / 2));
    }
    const surface = document.createElement('canvas');
    surface.width = width;
    surface.height = height;
    const context = surface.getContext('2d', { alpha: false });
    if (!context) return image;
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(image, 0, 0, width, height);
    return surface;
  };

  const chooseSources = (pano, maxTextureSize) => {
    const large = pano.dataset.kgmjSphereSrc || pano.dataset.kgmRoom360Src || '';
    const mobile = pano.dataset.kgmjSphereSrcMobile || pano.dataset.kgmRoom360SrcMobile || '';
    const largeWidth = Number(pano.dataset.kgmjSphereWidth || 4096);
    const compactDevice = matchMedia('(max-width: 900px)').matches ||
      Boolean(navigator.connection?.saveData) ||
      (Number(navigator.deviceMemory) > 0 && Number(navigator.deviceMemory) <= 4) ||
      maxTextureSize < largeWidth;
    const primary = compactDevice && mobile ? mobile : large || mobile;
    const secondary = primary === large ? mobile : large;
    return [...new Set([primary, secondary].filter(Boolean))];
  };

  const projectHotspots = (target, state, elements) => {
    const pano = resolvePano(target);
    if (!pano) return;
    const view = sanitizeState(pano, state);
    const rect = pano.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const aspect = Math.max(0.35, rect.width / rect.height);
    const verticalFov = radians(clamp(68 / view.zoom, 31, 100));
    const tangent = Math.tan(verticalFov / 2);
    const cameraYaw = radians(view.yaw);
    const cameraPitch = radians(view.pitch);
    const cy = Math.cos(cameraYaw);
    const sy = Math.sin(cameraYaw);
    const cp = Math.cos(cameraPitch);
    const sp = Math.sin(cameraPitch);

    getAnchors(pano, elements).forEach(element => {
      const anchorYaw = radians(Number(element.dataset.yaw || 0));
      const anchorPitch = radians(Number(element.dataset.pitch || 0));
      const cosPitch = Math.cos(anchorPitch);
      const worldX = Math.sin(anchorYaw) * cosPitch;
      const worldY = Math.sin(anchorPitch);
      const worldZ = Math.cos(anchorYaw) * cosPitch;

      // Inverse camera yaw, then inverse camera pitch.
      const yawX = cy * worldX - sy * worldZ;
      const yawZ = sy * worldX + cy * worldZ;
      const cameraY = cp * worldY - sp * yawZ;
      const cameraZ = sp * worldY + cp * yawZ;
      const ndcX = yawX / Math.max(0.0001, cameraZ * aspect * tangent);
      const ndcY = cameraY / Math.max(0.0001, cameraZ * tangent);
      const isProduct = element.matches('[data-kgmj-room-product]');
      const horizontalMargin = isProduct ? 1.34 : 1.12;
      const verticalMargin = isProduct ? 1.38 : 1.18;
      const visible = cameraZ > 0.035 && Math.abs(ndcX) <= horizontalMargin && Math.abs(ndcY) <= verticalMargin;

      element.hidden = !visible;
      element.classList.toggle('is-behind', !visible);
      element.setAttribute('aria-hidden', String(!visible));
      if (!visible) return;

      element.style.left = `${50 + ndcX * 50}%`;
      element.style.top = `${50 - ndcY * 50}%`;
      const depthFade = clamp((cameraZ - 0.04) / 0.42, 0, 1);

      if (isProduct) {
        const scale = Number(element.dataset.scale || 1);
        const depth = Number(element.dataset.depth || 1);
        const rotation = Number(element.dataset.rotation || 0);
        const tilt = Number(element.dataset.tilt || 0);
        const perspectiveScale = clamp(0.68 + cameraZ * 0.32, 0.58, 1.02);
        element.style.transform = `translate(-50%,-50%) perspective(700px) rotateZ(${rotation}deg) rotateX(${tilt}deg) scale(${scale * depth * perspectiveScale})`;
        element.style.opacity = String(depthFade);
      } else {
        element.style.transform = `translate(-50%,-50%) scale(${clamp(0.88 + cameraZ * 0.12, 0.82, 1.02)})`;
        element.style.opacity = String(depthFade);
      }
    });
  };

  const draw = instance => {
    if (!instance || instance.disposed || instance.contextLost || !instance.ready || activeInstance !== instance) return;
    const { pano, canvas, gl, program, position, uniforms, buffer, texture } = instance;
    const view = sanitizeState(pano, instance.state);
    instance.state = view;
    pano._kgmLast360State = view;
    resizeCanvas(instance);
    if (!canvas.width || !canvas.height) return;

    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.uniform1i(uniforms.texture, 0);
    gl.uniform1f(uniforms.aspect, canvas.width / Math.max(canvas.height, 1));
    gl.uniform1f(uniforms.vfov, radians(clamp(68 / view.zoom, 31, 100)));
    gl.uniform1f(uniforms.yaw, radians(view.yaw));
    gl.uniform1f(uniforms.pitch, radians(view.pitch));
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    projectHotspots(pano, view, instance.elements);
  };

  const requestDraw = instance => {
    if (!instance || instance.disposed) return;
    cancelAnimationFrame(instance.frame || 0);
    instance.frame = requestAnimationFrame(() => draw(instance));
  };

  const emitState = (instance, source) => {
    if (!instance || instance.disposed) return;
    instance.pano.dispatchEvent(new CustomEvent('kgm:room360-statechange', {
      bubbles: true,
      detail: { state: { ...instance.state }, source }
    }));
  };

  const resetView = (instance, source = 'reset') => {
    const defaults = initialState(instance.pano);
    Object.assign(instance.state, defaults);
    requestDraw(instance);
    emitState(instance, source);
  };

  const bindControls = instance => {
    const { pano, canvas } = instance;
    if (pano.dataset.kgmjControls === 'external') return;
    const signal = instance.controls.signal;
    let pointer = null;

    canvas.addEventListener('pointerdown', event => {
      if (event.button != null && event.button !== 0) return;
      canvas.focus({ preventScroll: true });
      pointer = {
        id: event.pointerId,
        type: event.pointerType,
        x: event.clientX,
        y: event.clientY,
        yaw: instance.state.yaw,
        pitch: instance.state.pitch,
        dragging: event.pointerType === 'mouse'
      };
      if (pointer.dragging) {
        canvas.setPointerCapture?.(event.pointerId);
        pano.classList.add('is-dragging');
        event.preventDefault();
      }
    }, { signal });

    canvas.addEventListener('pointermove', event => {
      if (!pointer || pointer.id !== event.pointerId) return;
      const dx = event.clientX - pointer.x;
      const dy = event.clientY - pointer.y;
      if (!pointer.dragging) {
        if (Math.abs(dx) < 7 && Math.abs(dy) < 7) return;
        // Let a vertical touchscreen gesture remain native page scrolling.
        if (pointer.type === 'touch' && Math.abs(dy) >= Math.abs(dx)) return;
        pointer.dragging = true;
        canvas.setPointerCapture?.(event.pointerId);
        pano.classList.add('is-dragging');
      }
      event.preventDefault();
      instance.state.yaw = normalizeAngle(pointer.yaw - dx * 0.16);
      instance.state.pitch = clamp(pointer.pitch + dy * 0.105, -72, 72);
      requestDraw(instance);
      emitState(instance, 'pointer');
    }, { signal, passive: false });

    const stopPointer = event => {
      if (!pointer || pointer.id !== event.pointerId) return;
      canvas.releasePointerCapture?.(pointer.id);
      pointer = null;
      pano.classList.remove('is-dragging');
    };
    canvas.addEventListener('pointerup', stopPointer, { signal });
    canvas.addEventListener('pointercancel', stopPointer, { signal });

    canvas.addEventListener('wheel', event => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      instance.state.zoom = clamp(instance.state.zoom - event.deltaY * 0.0007, 0.68, 2.2);
      requestDraw(instance);
      emitState(instance, 'wheel');
    }, { signal, passive: false });

    canvas.addEventListener('keydown', event => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const turnStep = event.shiftKey ? 12 : 6;
      const tiltStep = event.shiftKey ? 8 : 4;
      let handled = true;
      if (event.key === 'ArrowLeft') instance.state.yaw = normalizeAngle(instance.state.yaw - turnStep);
      else if (event.key === 'ArrowRight') instance.state.yaw = normalizeAngle(instance.state.yaw + turnStep);
      else if (event.key === 'ArrowUp') instance.state.pitch = clamp(instance.state.pitch + tiltStep, -72, 72);
      else if (event.key === 'ArrowDown') instance.state.pitch = clamp(instance.state.pitch - tiltStep, -72, 72);
      else if (event.key === '+' || event.key === '=') instance.state.zoom = clamp(instance.state.zoom + 0.1, 0.68, 2.2);
      else if (event.key === '-' || event.key === '_') instance.state.zoom = clamp(instance.state.zoom - 0.1, 0.68, 2.2);
      else if (event.key === 'Home' || event.key === '0') resetView(instance, 'keyboard');
      else handled = false;
      if (!handled) return;
      event.preventDefault();
      event.stopPropagation();
      requestDraw(instance);
      emitState(instance, 'keyboard');
    }, { signal });

    canvas.addEventListener('dblclick', () => resetView(instance), { signal });
  };

  const destroy = (instance, resetPresentation = true) => {
    if (!instance || instance.disposed) return;
    instance.disposed = true;
    cancelAnimationFrame(instance.frame || 0);
    instance.resizeObserver?.disconnect();
    instance.controls?.abort();
    instance.image.onload = null;
    instance.image.onerror = null;
    try { instance.image.removeAttribute('src'); } catch (_) {}
    if (!instance.contextLost) {
      try {
        instance.gl.deleteTexture(instance.texture);
        instance.gl.deleteBuffer(instance.buffer);
        instance.gl.deleteProgram(instance.program);
      } catch (_) {}
    }
    instances.delete(instance.pano);
    if (activeInstance === instance) activeInstance = null;
    if (resetPresentation) {
      instance.pano.classList.remove('is-ready', 'is-loading', 'is-error', 'is-context-lost', 'is-dragging');
      instance.pano.setAttribute('aria-busy', 'false');
      const loader = instance.pano.querySelector('[data-kgmj-sphere-loading]');
      if (loader) {
        loader.hidden = false;
        loader.removeAttribute('aria-hidden');
      }
    }
  };

  const dispose = target => {
    const pano = resolvePano(target);
    if (!pano) return;
    destroy(instances.get(pano));
  };

  const disposeOthers = target => {
    const pano = resolvePano(target);
    if (activeInstance && activeInstance.pano !== pano) destroy(activeInstance);
  };

  const loadTexture = instance => new Promise(resolve => {
    const { pano, gl, texture, image } = instance;
    const sources = chooseSources(pano, instance.maxTextureSize);
    let sourceIndex = 0;

    const fail = (reason, error) => {
      if (instance.disposed) return resolve(null);
      console.warn('[KGM Room 360] Falling back to room poster.', error || reason);
      showFallback(pano, 'Panoramic controls unavailable — showing room view', reason, error);
      resolve(null);
    };

    const trySource = () => {
      if (instance.disposed) return resolve(null);
      const source = sources[sourceIndex++];
      if (!source) return fail('image-load');
      image.onload = () => {
        if (instance.disposed) return resolve(null);
        const aspectRatio = image.naturalWidth / Math.max(image.naturalHeight, 1);
        if (aspectRatio < 1.95 || aspectRatio > 2.05) {
          if (sourceIndex < sources.length) return trySource();
          return fail('invalid-aspect-ratio', new Error('A true 360 panorama must use a 2:1 equirectangular image.'));
        }
        try {
          const uploadSource = fitTextureSource(image, instance.maxTextureSize);
          const uploadedWidth = uploadSource.width;
          const uploadedHeight = uploadSource.height;
          gl.bindTexture(gl.TEXTURE_2D, texture);
          gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, uploadSource);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

          if (isPowerOfTwoTexture(uploadSource)) {
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
            gl.hint(gl.GENERATE_MIPMAP_HINT, gl.NICEST);
            gl.generateMipmap(gl.TEXTURE_2D);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
            const anisotropy = gl.getExtension('EXT_texture_filter_anisotropic') ||
              gl.getExtension('WEBKIT_EXT_texture_filter_anisotropic') ||
              gl.getExtension('MOZ_EXT_texture_filter_anisotropic');
            if (anisotropy) {
              const maximum = gl.getParameter(anisotropy.MAX_TEXTURE_MAX_ANISOTROPY_EXT) || 1;
              gl.texParameterf(gl.TEXTURE_2D, anisotropy.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(8, maximum));
            }
          } else {
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
          }

          instance.ready = true;
          instance.source = source;
          // The GPU owns the uploaded pixels now; release the decoded DOM image
          // so an 8K room does not remain resident twice.
          image.onload = null;
          image.onerror = null;
          try { image.removeAttribute('src'); } catch (_) {}

          // V31.3: keep the wide static room poster visible until the WebGL
          // texture has produced a real frame. This avoids a dark/blank flash
          // and makes the transition into the interactive 360 feel continuous.
          requestDraw(instance);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (instance.disposed) return resolve(null);
              pano.classList.remove('is-loading', 'is-error', 'is-context-lost');
              pano.classList.add('is-ready');
              pano.setAttribute('aria-busy', 'false');
              const loader = pano.querySelector('[data-kgmj-sphere-loading]');
              if (loader) {
                loader.hidden = true;
                loader.setAttribute('aria-hidden', 'true');
              }
              pano.dispatchEvent(new CustomEvent('kgm:room360-ready', {
                bubbles: true,
                detail: {
                  source,
                  width: uploadedWidth,
                  height: uploadedHeight,
                  room: pano.closest?.('[data-kgmj-scene]')?.dataset.kgmjScene || ''
                }
              }));
              resolve(instance);
            });
          });
        } catch (error) {
          if (sourceIndex < sources.length) trySource();
          else fail('texture-upload', error);
        }
      };
      image.onerror = () => {
        if (sourceIndex < sources.length) trySource();
        else fail('image-load');
      };
      image.src = source;
    };

    trySource();
  });

  const ensure = (target, state, elements) => {
    const pano = resolvePano(target);
    if (!pano) return Promise.resolve(null);
    const view = sanitizeState(pano, state);
    const anchors = getAnchors(pano, elements);
    pano._kgmLast360State = view;

    let instance = instances.get(pano);
    if (instance && !instance.disposed) {
      instance.state = view;
      instance.elements = anchors;
      if (instance.ready) requestDraw(instance);
      return instance.promise || Promise.resolve(instance);
    }

    disposeOthers(pano);
    const canvas = pano.querySelector('[data-kgmj-sphere-canvas]');
    const source = pano.dataset.kgmjSphereSrc || pano.dataset.kgmjSphereSrcMobile || pano.dataset.kgmRoom360Src;
    if (!canvas || !source) {
      showFallback(pano, 'Panoramic controls unavailable — showing room view', 'missing-markup');
      return Promise.resolve(null);
    }

    showLoading(pano);
    canvas.tabIndex = canvas.hasAttribute('tabindex') ? canvas.tabIndex : 0;
    const roomName = pano.closest?.('[data-kgmj-scene]')?.dataset.title || pano.dataset.roomLabel || 'room';
    if (!canvas.hasAttribute('aria-label')) {
      canvas.setAttribute('aria-label', `Interactive 360 degree view of ${roomName}. Drag horizontally or use arrow keys to look around. Use plus and minus to zoom.`);
    }
    canvas.setAttribute('role', 'img');

    let gl;
    let program;
    try {
      gl = canvas.getContext('webgl', {
        antialias: false,
        alpha: false,
        depth: false,
        stencil: false,
        preserveDrawingBuffer: false,
        powerPreference: 'high-performance'
      });
      if (!gl) throw new Error('WebGL is unavailable.');
      program = createProgram(gl);
    } catch (error) {
      showFallback(pano, 'Panoramic controls unavailable — showing room view', 'webgl', error);
      return Promise.resolve(null);
    }

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1, -1, 1, -1, -1, 1,
      -1, 1, 1, -1, 1, 1
    ]), gl.STATIC_DRAW);
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(
      gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0,
      gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([8, 9, 12, 255])
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    instance = {
      pano,
      canvas,
      gl,
      program,
      buffer,
      texture,
      position: gl.getAttribLocation(program, 'a_position'),
      uniforms: {
        texture: gl.getUniformLocation(program, 'u_texture'),
        aspect: gl.getUniformLocation(program, 'u_aspect'),
        vfov: gl.getUniformLocation(program, 'u_vfov'),
        yaw: gl.getUniformLocation(program, 'u_yaw'),
        pitch: gl.getUniformLocation(program, 'u_pitch')
      },
      state: view,
      elements: anchors,
      image: new Image(),
      ready: false,
      disposed: false,
      contextLost: false,
      controls: new AbortController(),
      maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE) || 2048,
      frame: 0,
      resizeObserver: null,
      promise: null
    };
    instance.image.decoding = 'async';
    instance.image.crossOrigin = 'anonymous';
    instances.set(pano, instance);
    activeInstance = instance;
    resizeCanvas(instance);
    bindControls(instance);

    const contextLost = event => {
      event.preventDefault();
      if (instance.disposed) return;
      instance.contextLost = true;
      instance.ready = false;
      pano.classList.remove('is-ready', 'is-loading');
      pano.classList.add('is-context-lost', 'is-error');
      showFallback(pano, 'Panoramic controls paused — showing room view', 'context-lost');
    };
    const contextRestored = () => {
      if (instance.disposed) return;
      const savedState = instance.state;
      const savedElements = instance.elements;
      destroy(instance, false);
      queueMicrotask(() => ensure(pano, savedState, savedElements));
    };
    canvas.addEventListener('webglcontextlost', contextLost, { signal: instance.controls.signal });
    canvas.addEventListener('webglcontextrestored', contextRestored, { signal: instance.controls.signal });

    if ('ResizeObserver' in window) {
      instance.resizeObserver = new ResizeObserver(() => requestDraw(instance));
      instance.resizeObserver.observe(pano);
    }

    instance.promise = loadTexture(instance);
    return instance.promise;
  };

  const render = (target, state, elements) => {
    const pano = resolvePano(target);
    if (!pano) return Promise.resolve(null);
    const view = sanitizeState(pano, state);
    pano._kgmLast360State = view;
    const instance = instances.get(pano);
    if (instance && !instance.disposed) {
      instance.state = view;
      instance.elements = getAnchors(pano, elements);
      requestDraw(instance);
      return instance.promise || Promise.resolve(instance);
    }
    return ensure(pano, view, elements);
  };

  const room360Api = Object.freeze({
    ensure,
    render,
    dispose,
    disposeOthers,
    projectHotspots,
    isPowerOfTwoTexture
  });
  window.KGMRoom360 = room360Api;
  // Keep the V30 Living Room API name working for existing theme integrations.
  window.KGMLiving360 = room360Api;
})();
